import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Info, AlertTriangle, ShieldAlert, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { PeriodLockPanel } from "@/components/PeriodLockPanel";

export default function SettingsPage() {
  const [form, setForm] = useState(null);
  const [dangerOpen, setDangerOpen] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const [launchKey, setLaunchKey] = useState(0);
  const { user } = useAuth();
  const isSuperAdmin = (user?.role_id || user?.role) === "super_admin";

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  const save = async () => {
    try { await api.patch("/settings", form); toast.success("Settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!form) return null;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="uc-label">Configuration</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Settings</h1>
      </div>

      <Tabs defaultValue="company">
        <TabsList className="bg-[#F7F2E8]">
          <TabsTrigger value="company" data-testid="settings-tab-company">Company</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="settings-tab-integrations">Integrations</TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="danger" data-testid="settings-tab-danger" className="data-[state=active]:bg-red-100 data-[state=active]:text-red-800">
              Governance &amp; Danger Zone
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="company" className="pt-4">
          <div className="bg-white border border-[#EAE5D9] rounded-lg p-6 space-y-4">
            <div><Label>Company Name</Label><Input data-testid="settings-name-input" value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>GSTIN</Label><Input data-testid="settings-gstin-input" value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
              <div><Label>FSSAI Licence No.</Label><Input data-testid="settings-fssai-input" placeholder="14-digit licence" value={form.fssai_license || ""} onChange={(e) => setForm({ ...form, fssai_license: e.target.value })} /></div>
              <div><Label>Invoice Prefix</Label><Input value={form.invoice_prefix || ""} onChange={(e) => setForm({ ...form, invoice_prefix: e.target.value })} /></div>
            </div>
            <p className="text-xs text-[#8B7355] -mt-2">GSTIN, FSSAI, address and phone print on every POS Tax Invoice receipt.</p>
            <div><Label>Address</Label><Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>City</Label><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>State</Label><Input value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div><Label>Pincode</Label><Input value={form.pincode || ""} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Default GST rate (%)</Label>
                <Input
                  type="number" step="0.01" min="0" max="28"
                  value={form.default_gst_rate ?? ""}
                  onChange={(e) => setForm({ ...form, default_gst_rate: e.target.value === "" ? null : Number(e.target.value) })}
                  data-testid="settings-default-gst-input"
                  placeholder="e.g. 18"
                />
                <div className="text-[11px] text-[#8B7355] mt-1">
                  Used when a product line has no GST rate configured. MRP on the POS is treated as tax-inclusive; this rate is used to back-calculate the base price + CGST + SGST.
                </div>
              </div>
              <div><Label>Logo URL</Label><Input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-[#EAE5D9]">
              <div>
                <Label>UPI ID for bill QR code</Label>
                <Input data-testid="settings-upi-id" placeholder="bakemill@okaxis" value={form.upi_id || ""} onChange={(e) => setForm({ ...form, upi_id: e.target.value.trim() })} />
                <p className="text-[11px] text-[#8B7355] mt-1">Printed POS bills show a scan-to-pay QR for the exact bill amount (upi://pay). Leave blank to hide the QR.</p>
              </div>
              <div>
                <Label>UPI payee name (optional)</Label>
                <Input data-testid="settings-upi-name" placeholder="Bakemill Foods" value={form.upi_payee_name || ""} onChange={(e) => setForm({ ...form, upi_payee_name: e.target.value })} />
              </div>
            </div>
            <Button onClick={save} data-testid="settings-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save Changes</Button>
          </div>
        </TabsContent>

        <TabsContent value="integrations" className="pt-4">
          <div className="bg-white border border-[#EAE5D9] rounded-lg p-6 space-y-6">
            <div className="flex items-start gap-2 bg-[#FEEBC8] border border-[#F6AD55] rounded-md p-3 text-sm">
              <Info className="w-4 h-4 text-[#7B341E] mt-0.5 shrink-0" />
              <div>Keys are stored in your Bakemill database. They are used server-side only to send messages on your behalf. You can leave these blank and add them anytime.</div>
            </div>

            <section className="space-y-3">
              <h3 className="font-[Outfit] text-lg font-semibold">Email · Resend</h3>
              <p className="text-xs text-[#8B7355]">Sign up at <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-[#C05621] underline">resend.com</a> → API Keys → Create.</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Resend API Key</Label><Input type="password" placeholder="re_..." data-testid="settings-resend-key" value={form.resend_api_key || ""} onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })} /></div>
                <div><Label>Sender Email (verified)</Label><Input placeholder="orders@bakemill.com or onboarding@resend.dev" data-testid="settings-sender-email" value={form.sender_email || ""} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} /></div>
              </div>
            </section>

            <section className="space-y-3 pt-4 border-t border-[#EAE5D9]">
              <h3 className="font-[Outfit] text-lg font-semibold">SMS & WhatsApp · Twilio</h3>
              <p className="text-xs text-[#8B7355]">From <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-[#C05621] underline">console.twilio.com</a>. For WhatsApp testing use sandbox <code>whatsapp:+14155238886</code>.</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Account SID</Label><Input placeholder="AC..." data-testid="settings-twilio-sid" value={form.twilio_account_sid || ""} onChange={(e) => setForm({ ...form, twilio_account_sid: e.target.value })} /></div>
                <div><Label>Auth Token</Label><Input type="password" data-testid="settings-twilio-token" value={form.twilio_auth_token || ""} onChange={(e) => setForm({ ...form, twilio_auth_token: e.target.value })} /></div>
                <div><Label>SMS Sender (E.164)</Label><Input placeholder="+15555550100" value={form.twilio_sms_from || ""} onChange={(e) => setForm({ ...form, twilio_sms_from: e.target.value })} /></div>
                <div><Label>WhatsApp Sender</Label><Input placeholder="whatsapp:+14155238886" value={form.twilio_whatsapp_from || ""} onChange={(e) => setForm({ ...form, twilio_whatsapp_from: e.target.value })} /></div>
              </div>
            </section>

            <section className="space-y-3 pt-4 border-t border-[#EAE5D9]">
              <h3 className="font-[Outfit] text-lg font-semibold">Automation</h3>
              <p className="text-xs text-[#8B7355]">Requires Resend key above. Stock guard blocks invoices with insufficient product stock.</p>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 bg-[#F7F2E8] rounded-md cursor-pointer">
                  <Switch checked={!!form.strict_stock} onCheckedChange={(v) => setForm({ ...form, strict_stock: v })} data-testid="settings-strict-stock" />
                  <div>
                    <div className="font-medium text-sm">Refuse invoice when insufficient stock</div>
                    <div className="text-xs text-[#8B7355]">Blocks selling more than what's available in Products.</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 bg-[#F7F2E8] rounded-md cursor-pointer">
                  <Switch checked={!!form.auto_email_invoice} onCheckedChange={(v) => setForm({ ...form, auto_email_invoice: v })} data-testid="settings-auto-invoice" />
                  <div>
                    <div className="font-medium text-sm">Auto-email invoice PDF to customer on creation</div>
                    <div className="text-xs text-[#8B7355]">Customer must have an email on record.</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 bg-[#F7F2E8] rounded-md cursor-pointer">
                  <Switch checked={!!form.auto_email_quotation} onCheckedChange={(v) => setForm({ ...form, auto_email_quotation: v })} data-testid="settings-auto-quote" />
                  <div>
                    <div className="font-medium text-sm">Auto-email quotation PDF to customer on creation</div>
                    <div className="text-xs text-[#8B7355]">Great for fast turnaround with new leads.</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 bg-[#F7F2E8] rounded-md cursor-pointer">
                  <Switch checked={!!form.auto_whatsapp_pos} onCheckedChange={(v) => setForm({ ...form, auto_whatsapp_pos: v })} data-testid="settings-auto-whatsapp-pos" />
                  <div>
                    <div className="font-medium text-sm">Auto-send POS invoice on WhatsApp</div>
                    <div className="text-xs text-[#8B7355]">Requires Twilio credentials above + customer/walk-in phone captured at checkout.</div>
                  </div>
                </label>
              </div>
            </section>

            <Button onClick={save} data-testid="settings-save-integrations-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save Integration Keys</Button>
          </div>
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="danger" className="pt-4 space-y-6">
            <PeriodLockPanel />
            <LaunchWipePanel onOpen={() => setLaunchOpen(true)} key={launchKey} />
            <FullWipePanel onOpen={() => setFullOpen(true)} key={`full-${launchKey}`} />
            <DangerZonePanel onOpen={() => setDangerOpen(true)} />
          </TabsContent>
        )}
      </Tabs>
      {dangerOpen && <FactoryResetDialog onClose={() => setDangerOpen(false)} />}
      {launchOpen && <LaunchWipeDialog onClose={() => { setLaunchOpen(false); setLaunchKey((k) => k + 1); }} />}
      {fullOpen && <FullWipeDialog onClose={() => { setFullOpen(false); setLaunchKey((k) => k + 1); }} />}
    </div>
  );
}

// ==================== Danger Zone ====================
function DangerZonePanel({ onOpen }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/factory-reset/preview");
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load preview"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="border-2 border-red-200 rounded-lg bg-red-50/40 p-6 space-y-5" data-testid="danger-zone-panel">
      <div className="flex items-start gap-3">
        <ShieldAlert className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="font-[Outfit] text-xl font-semibold text-red-900">Factory Reset — Pre-Launch Wipe</h2>
          <p className="text-sm text-red-800/90 mt-1">
            Permanently deletes every transaction, invoice, stock movement, ledger entry, voucher, salary, loyalty ledger row, audit trail, and the entire CRM (customers, leads, followups) — and resets product/ingredient stock and bank opening balances to zero. Also permanently removes leftover QA/test user logins, keeping only Super Admin + genuine staff accounts. Masters (roles, branches, products, recipes, settings) are preserved. <b>This cannot be undone.</b>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Rows to wipe" value={preview ? preview.total_rows_wiped.toLocaleString() : "…"} tone="red" />
        <StatCard label="Collections" value={preview ? preview.would_wipe.length : "…"} tone="red" />
        <StatCard label="SKUs with stock" value={preview ? preview.would_reset.products_with_current_stock : "…"} tone="amber" />
        <StatCard label="Test users to delete" value={preview ? preview.would_delete_users : "…"} tone="amber" />
      </div>
      {preview && (
        <p className="text-xs text-red-800/70">
          {preview.users_kept} staff/admin login(s) will be kept. Customers, leads and followups will all be permanently deleted (full CRM wipe).
        </p>
      )}

      {preview && preview.would_wipe.length > 0 && (
        <details className="bg-white border border-red-200 rounded-md p-3">
          <summary className="text-sm font-medium text-red-900 cursor-pointer">Show full wipe list ({preview.would_wipe.length} collections)</summary>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 mt-3 text-xs font-mono text-red-800/80 max-h-72 overflow-y-auto">
            {[...preview.would_wipe].sort((a, b) => b.count - a.count).map((r) => (
              <div key={r.collection} className="flex justify-between">
                <span>{r.collection}</span><span className="tabular-nums">{r.count}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="danger-refresh-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Refresh preview
        </Button>
        <Button onClick={onOpen} className="bg-red-600 hover:bg-red-700 text-white" data-testid="danger-open-dialog-btn" disabled={!preview}>
          <AlertTriangle className="w-4 h-4 mr-1.5" /> Factory Reset Now
        </Button>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "neutral" }) {
  const map = {
    red: "border-red-200 bg-white text-red-900",
    amber: "border-amber-200 bg-white text-amber-900",
    emerald: "border-emerald-200 bg-white text-emerald-900",
    neutral: "border-slate-200 bg-white text-slate-900",
  };
  return (
    <div className={`rounded-md border p-3 ${map[tone]}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">{label}</div>
      <div className="text-2xl font-semibold font-[Outfit] tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function FactoryResetDialog({ onClose }) {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const REQUIRED = "WIPE_ALL_TRANSACTIONS";
  const fire = async () => {
    if (phrase !== REQUIRED) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/factory-reset", { confirmation: REQUIRED });
      setResult(r.data);
      toast.success(`Wiped ${r.data.total_rows_wiped.toLocaleString()} rows. Ready for launch.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Confirm Factory Reset
          </DialogTitle>
          <DialogDescription>
            This will permanently delete every transactional record and cannot be undone. To proceed, type <b className="font-mono">{REQUIRED}</b> below.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3 py-2">
            <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm">
              <div className="font-semibold">Reset complete.</div>
              <div className="mt-1">Wiped <b>{result.total_rows_wiped.toLocaleString()}</b> rows across <b>{Object.keys(result.wiped).length}</b> collections at {result.finished_at?.slice(0, 19).replace('T', ' ')} UTC.</div>
              <ul className="mt-2 text-xs list-disc pl-5 space-y-0.5">
                <li>{result.resets.products_stock_zeroed} product stock levels reset to 0</li>
                <li>{result.resets.ingredients_stock_zeroed} ingredient stock levels reset to 0</li>
                <li>{result.resets.bank_accounts_zeroed} bank opening balances reset to 0</li>
                <li>{result.users_deleted} QA/test user login(s) permanently deleted</li>
                <li>Customers, leads and followups permanently deleted (full CRM wipe)</li>
              </ul>
            </div>
            <div className="text-xs text-slate-500">Reload the dashboard — Total Sales / Revenue will now read ₹0.</div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <Label className="text-red-900">Type the confirmation phrase to unlock</Label>
            <Input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={REQUIRED}
              className="font-mono border-red-300"
              autoFocus
              data-testid="danger-confirm-input"
            />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="danger-cancel-btn">{result ? "Close" : "Cancel"}</Button>
          {!result && (
            <Button
              onClick={fire}
              disabled={phrase !== REQUIRED || busy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="danger-fire-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Wiping…</> : "Wipe & Reset Balances"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Launch Wipe (financial + stock only) ====================
function LaunchWipePanel({ onOpen }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/factory-reset/preview?mode=launch");
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load preview"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const m = preview?.masters_counts || {};
  return (
    <div className="border-2 border-amber-300 rounded-lg bg-amber-50/40 p-6 space-y-5" data-testid="launch-wipe-panel">
      <div className="flex items-start gap-3">
        <ShieldAlert className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="font-[Outfit] text-xl font-semibold text-amber-900">Launch Wipe — Clear Financial &amp; Stock Data Only</h2>
          <p className="text-sm text-amber-900/90 mt-1">
            Permanently deletes all Sales Invoices (POS + manual + B2B), Purchase Bills &amp; Vendor Purchases, GRNs, Dispatches, Stock Transfers, Stock Movements &amp; Batches, Journal Entries, Vouchers, Expenses, Payments, POS Day-Closes and the Loyalty points ledger. Zeroes stock at every location and bank opening balances so the Ledger, P&amp;L and Balance Sheet read ₹0.00. Document numbering restarts at 0001.
            <b> Keeps</b> Products, Vendors, Customers (profiles kept, loyalty points reset), Branches, Users &amp; Roles, Employees/HR, QC records, Audit Log, Chart of Accounts and all Settings. Removes QA/test logins only. <b>This cannot be undone.</b>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Rows to wipe" value={preview ? preview.total_rows_wiped.toLocaleString() : "…"} tone="amber" />
        <StatCard label="Stock rows to zero" value={preview ? preview.would_reset.branch_stock_rows : "…"} tone="amber" />
        <StatCard label="Products kept" value={preview ? m.products : "…"} tone="emerald" />
        <StatCard label="Vendors / Customers kept" value={preview ? `${m.vendors} / ${m.customers}` : "…"} tone="emerald" />
      </div>
      {preview && (
        <p className="text-xs text-amber-900/70" data-testid="launch-wipe-summary">
          {preview.users_kept} staff login(s) kept · {preview.would_delete_users} QA/test login(s) will be removed · {m.branches} branches · {m.employees} employees untouched.
        </p>
      )}

      {preview && preview.would_wipe.length > 0 && (
        <details className="bg-white border border-amber-200 rounded-md p-3">
          <summary className="text-sm font-medium text-amber-900 cursor-pointer">Show wipe list ({preview.would_wipe.length} collections)</summary>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 mt-3 text-xs font-mono text-amber-900/80 max-h-72 overflow-y-auto">
            {[...preview.would_wipe].sort((a, b) => b.count - a.count).map((r) => (
              <div key={r.collection} className="flex justify-between">
                <span>{r.collection}</span><span className="tabular-nums">{r.count}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="launch-wipe-refresh-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Refresh preview
        </Button>
        <Button onClick={onOpen} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="launch-wipe-open-btn" disabled={!preview}>
          <AlertTriangle className="w-4 h-4 mr-1.5" /> Launch Wipe (keep masters &amp; customers)
        </Button>
      </div>
    </div>
  );
}

function LaunchWipeDialog({ onClose }) {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const REQUIRED = "WIPE_ALL_TRANSACTIONS";
  const fire = async () => {
    if (phrase !== REQUIRED) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/factory-reset", { confirmation: REQUIRED, mode: "launch" });
      setResult(r.data);
      toast.success(`Launch Wipe done — ${r.data.total_rows_wiped.toLocaleString()} rows cleared, masters intact.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const mr = result?.masters_remaining || {};
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Confirm Launch Wipe
          </DialogTitle>
          <DialogDescription>
            Deletes every financial and stock transaction, zeroes all stock and bank balances, and keeps Products, Vendors, Customers, Branches, Users, HR, QC and Audit Log. Type <b className="font-mono">{REQUIRED}</b> to proceed.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3 py-2" data-testid="launch-wipe-result">
            <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm">
              <div className="font-semibold">Launch Wipe complete — books are at ₹0.00.</div>
              <div className="mt-1">Cleared <b>{result.total_rows_wiped.toLocaleString()}</b> rows across <b>{Object.keys(result.wiped).length}</b> collections at {result.finished_at?.slice(0, 19).replace('T', ' ')} UTC.</div>
              <ul className="mt-2 text-xs list-disc pl-5 space-y-0.5">
                <li>{result.resets.products_stock_zeroed} products · stock zeroed at every location</li>
                <li>{result.resets.bank_accounts_zeroed} bank opening balances reset to ₹0</li>
                <li>{result.resets.customers_loyalty_zeroed} customer loyalty balances reset to 0 (profiles kept)</li>
                <li>{result.users_deleted} QA/test login(s) removed</li>
              </ul>
              <div className="mt-3 text-xs font-medium">Masters intact: {mr.products} products · {mr.vendors} vendors · {mr.customers} customers · {mr.branches} branches · {mr.users} users · {mr.roles} roles · {mr.employees} employees · {mr.chart_of_accounts} ledger accounts · {mr.audit_logs} audit rows</div>
            </div>
            <div className="text-xs text-slate-500">Reload the dashboard — Sales, Ledger, P&amp;L and Balance Sheet will now read ₹0.00 and the next invoice starts at 0001.</div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <Label className="text-amber-900">Type the confirmation phrase to unlock</Label>
            <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={REQUIRED} className="font-mono border-amber-300" autoFocus data-testid="launch-wipe-confirm-input" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="launch-wipe-cancel-btn">{result ? "Close" : "Cancel"}</Button>
          {!result && (
            <Button onClick={fire} disabled={phrase !== REQUIRED || busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="launch-wipe-fire-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Wiping…</> : "Wipe Financial & Stock Data"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Full Wipe (everything except configuration) ====================
function FullWipePanel({ onOpen }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/factory-reset/preview?mode=wipe_all");
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load preview"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const m = preview?.masters_counts || {};
  const nonEmpty = preview ? preview.would_wipe.filter((r) => r.count > 0) : [];
  return (
    <div className="border-2 border-[#2C1810] rounded-lg bg-[#2C1810] text-[#F7F2E8] p-6 space-y-5" data-testid="full-wipe-panel">
      <div className="flex items-start gap-3">
        <ShieldAlert className="w-6 h-6 text-red-300 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="font-[Outfit] text-xl font-semibold">Full Wipe — Start Blank (keep configuration only)</h2>
          <p className="text-sm text-[#F7F2E8]/85 mt-1">
            Deletes <b>every</b> data collection: all transactions <i>and</i> all masters — Products &amp; catalogue, Ingredients / raw materials, Vendors, Customers, Leads, Employees &amp; HR, QC SOPs / specs / lab, BOMs &amp; recipes, campaigns, tasks, forecasts, notifications and the audit log.
            <b> Keeps only</b> user logins, roles, company settings (GSTIN/FSSAI/receipt), chart of accounts, branches/stores and bank accounts (balances zeroed), plus deals/loyalty/markdown/QC-digest settings, mobile categories and shift patterns. <b>This cannot be undone.</b>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Rows to wipe" value={preview ? preview.total_rows_wiped.toLocaleString() : "…"} tone="red" />
        <StatCard label="Collections with data" value={preview ? nonEmpty.length : "…"} tone="red" />
        <StatCard label="Products / Vendors (will go)" value={preview ? `${m.products} / ${m.vendors}` : "…"} tone="red" />
        <StatCard label="Branches kept · Logins kept" value={preview ? `${m.branches} · ${preview.users_kept}` : "…"} tone="emerald" />
      </div>
      {preview && (
        <p className="text-xs text-[#F7F2E8]/70" data-testid="full-wipe-summary">
          {m.customers} customers and {m.employees} employees will also be deleted · {preview.would_delete_users} QA/test login(s) removed · {preview.users_kept} staff login(s) kept.
        </p>
      )}

      {preview && (
        <details className="bg-white/5 border border-white/15 rounded-md p-3" open>
          <summary className="text-sm font-medium cursor-pointer">Per-collection count preview ({nonEmpty.length} with data, {preview.would_wipe.length} total)</summary>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 mt-3 text-xs font-mono text-[#F7F2E8]/85 max-h-72 overflow-y-auto" data-testid="full-wipe-count-list">
            {[...preview.would_wipe].sort((a, b) => b.count - a.count).map((r) => (
              <div key={r.collection} className={`flex justify-between ${r.count === 0 ? "opacity-40" : ""}`}>
                <span>{r.collection}</span><span className="tabular-nums">{r.count}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={load} variant="outline" size="sm" disabled={loading} className="bg-transparent border-white/30 text-[#F7F2E8] hover:bg-white/10 hover:text-white" data-testid="full-wipe-refresh-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Refresh preview
        </Button>
        <Button onClick={onOpen} className="bg-red-600 hover:bg-red-700 text-white" data-testid="full-wipe-open-btn" disabled={!preview}>
          <AlertTriangle className="w-4 h-4 mr-1.5" /> Full Wipe (keep config only)
        </Button>
      </div>
    </div>
  );
}

function FullWipeDialog({ onClose }) {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const REQUIRED = "WIPE_ALL_TRANSACTIONS";
  const fire = async () => {
    if (phrase !== REQUIRED) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/factory-reset", { confirmation: REQUIRED, mode: "wipe_all" });
      setResult(r.data);
      toast.success(`Full Wipe done — ${r.data.total_rows_wiped.toLocaleString()} rows cleared.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const remaining = result ? Object.entries(result.remaining_counts || {}) : [];
  const kept = new Set(result?.kept_collections || []);
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Confirm Full Wipe
          </DialogTitle>
          <DialogDescription>
            Deletes ALL data including Products, Ingredients, Vendors, Customers, Employees and every transaction. Only logins, roles, settings, chart of accounts, branches and bank accounts survive. Type <b className="font-mono">{REQUIRED}</b> to proceed.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3 py-2" data-testid="full-wipe-result">
            <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm">
              <div className="font-semibold">Full Wipe complete — database is blank except configuration.</div>
              <div className="mt-1">Cleared <b>{result.total_rows_wiped.toLocaleString()}</b> rows across <b>{Object.keys(result.wiped).length}</b> collections · {result.resets.bank_accounts_zeroed} bank balances zeroed · {result.users_deleted} QA login(s) removed.</div>
            </div>
            <div className="text-xs font-semibold text-slate-700">Remaining document count per collection</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-0.5 text-xs font-mono max-h-64 overflow-y-auto border rounded-md p-3" data-testid="full-wipe-remaining-list">
              {remaining.sort((a, b) => b[1] - a[1]).map(([c, n]) => (
                <div key={c} className={`flex justify-between ${n === 0 ? "text-slate-400" : kept.has(c) ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}`}>
                  <span>{c}</span><span className="tabular-nums">{n}</span>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-500">Green = kept configuration · amber = audit row of this wipe · grey = empty.</div>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <Label className="text-red-800">Type the confirmation phrase to unlock</Label>
            <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={REQUIRED} className="font-mono border-red-300" autoFocus data-testid="full-wipe-confirm-input" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="full-wipe-cancel-btn">{result ? "Close" : "Cancel"}</Button>
          {!result && (
            <Button onClick={fire} disabled={phrase !== REQUIRED || busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="full-wipe-fire-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Wiping…</> : "Wipe Everything Except Configuration"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TwoFactorPanel() {
  const [status, setStatus] = React.useState(null);
  const [setupData, setSetupData] = React.useState(null);
  const [code, setCode] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [backupCodes, setBackupCodes] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = async () => {
    const { data } = await api.get("/auth/2fa/status");
    setStatus(data);
  };
  React.useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/setup"); setSetupData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Setup failed"); }
    finally { setBusy(false); }
  };

  const verifyAndEnable = async () => {
    if (!code.trim()) return toast.error("Enter the 6-digit code from your authenticator");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { otp_code: code.trim() });
      toast.success("2FA enabled — save your backup codes below");
      setBackupCodes(data.backup_codes);
      setSetupData(null);
      setCode("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!password || !code) return toast.error("Password and current OTP required to disable");
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password, otp_code: code });
      toast.success("2FA disabled");
      setPassword(""); setCode(""); setBackupCodes(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disable failed"); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="text-sm text-[#8B7355]">Loading…</div>;

  return (
    <div className="bg-white border border-[#EAE5D9] rounded-lg p-6 space-y-5" data-testid="two-factor-panel">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${status.enabled ? "bg-emerald-100 text-emerald-700" : "bg-[#FEEBC8] text-[#C05621]"}`}>
          {status.enabled ? "✓" : "!"}
        </div>
        <div className="flex-1">
          <div className="font-[Outfit] text-lg font-semibold" data-testid="two-factor-status">
            Two-Factor Authentication ({status.enabled ? "Enabled" : "Disabled"})
          </div>
          <p className="text-sm text-[#5C4033] mt-1">
            {status.enabled
              ? `Active since ${new Date(status.enabled_at).toLocaleDateString("en-IN")}. ${status.backup_codes_remaining} backup code${status.backup_codes_remaining === 1 ? "" : "s"} remaining.`
              : "Strongly recommended for Super Admin & Admin accounts. Adds a one-time code from your phone at login."}
          </p>
        </div>
      </div>

      {!status.enabled && !setupData && (
        <div>
          <Button onClick={startSetup} disabled={busy} data-testid="two-factor-setup-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
            {busy ? "Preparing…" : "Enable Two-Factor"}
          </Button>
        </div>
      )}

      {!status.enabled && setupData && (
        <div className="grid md:grid-cols-2 gap-6 p-4 border border-[#EAE5D9] rounded-md bg-[#FDFBF7]" data-testid="two-factor-setup-block">
          <div>
            <div className="uc-label mb-2">1 · Scan with your authenticator</div>
            <img src={setupData.qr_data_url} alt="TOTP QR" className="w-48 h-48 rounded-md border border-[#EAE5D9] bg-white p-2" data-testid="two-factor-qr" />
            <details className="mt-3 text-xs text-[#5C4033]">
              <summary className="cursor-pointer">Can't scan? Show secret</summary>
              <div className="mt-2 font-mono bg-white border border-[#EAE5D9] rounded p-2 break-all">{setupData.secret}</div>
            </details>
          </div>
          <div className="space-y-3">
            <div className="uc-label">2 · Enter the 6-digit code</div>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              inputMode="numeric"
              className="font-mono tracking-[0.3em] text-center text-lg"
              data-testid="two-factor-verify-input"
            />
            <Button onClick={verifyAndEnable} disabled={busy} data-testid="two-factor-verify-btn" className="w-full bg-[#C05621] hover:bg-[#9C4221] text-white">
              {busy ? "Verifying…" : "Verify & enable"}
            </Button>
          </div>
        </div>
      )}

      {backupCodes && (
        <div className="p-4 border border-amber-300 bg-amber-50 rounded-md" data-testid="two-factor-backup-codes">
          <div className="uc-label !text-amber-800 mb-2">⚠️ Save these backup codes now</div>
          <p className="text-xs text-amber-800 mb-3">Each code works ONCE. If you lose your authenticator, use one to sign in. They won't be shown again.</p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes.map((c) => <div key={c} className="bg-white border border-amber-200 rounded px-3 py-1.5 text-center">{c}</div>)}
          </div>
        </div>
      )}

      {status.enabled && (
        <div className="p-4 border border-red-200 rounded-md bg-red-50 space-y-3">
          <div className="font-medium text-sm text-red-900">Disable 2FA</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Current password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="two-factor-disable-password" />
            </div>
            <div>
              <Label className="text-xs">Current OTP or backup code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" data-testid="two-factor-disable-otp" />
            </div>
          </div>
          <Button onClick={disable} disabled={busy} variant="outline" data-testid="two-factor-disable-btn" className="border-red-300 text-red-700 hover:bg-red-100">
            {busy ? "Disabling…" : "Disable Two-Factor"}
          </Button>
        </div>
      )}
    </div>
  );
}
